import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { userSchemas } from 'schemas';
import { userSelectors } from 'reducers';
import {
    useGetRolesQuery,
    useGetUsersQuery,
    useUpdateConventionMutation,
    useUpdateSignatureMutation,
} from 'api/main';
import { jsonParseSafe, parseFullName } from 'utils/helpers';
import { signatureDialogSet } from 'reducers/user';
import { RoleNames, roleCategories, signatoryUserType, stepIDs } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    CancelButton,
    CustomDialogTitle,
    CustomSelect,
    Loader,
    RadioLabel,
} from 'components';
import { Box, Dialog, DialogActions, DialogContent, MenuItem, RadioGroup } from '@mui/material';
import { getUserIdFromIri } from 'utils/helpers/iri';
import { downloadClick } from 'utils/helpers/documents';
import { useUserRoleMatchCategory } from 'utils/hooks/subscriptions';

const SignatureChoiceDialog = () => {
    const dispatch = useDispatch();
    const [updateConvention, { isLoading: conventionLoader, isSuccess, reset }] =
        useUpdateConventionMutation();
    const [
        updateSignature,
        { isLoading: signatureLoader, isSuccess: signatureSuccess, reset: resetSignature },
    ] = useUpdateSignatureMutation();
    const { open, subscription, convention } = useSelector(userSelectors.getSignatureDialogState);

    const internalUser = useSelector(userSelectors.getInternalUser);
    const userIsProvider = useUserRoleMatchCategory(roleCategories.PROVIDERS);

    const [selectedUser, setSelectedUser] = useState('');
    const [signatureOption, setSignatureOption] = useState('me');
    const [error, setError] = useState('');

    const servicerSignature = useMemo(() => {
        const signatureFormStep = subscription?.userTunnelSteps?.find(
            us => us.tunnelStep.step.id === stepIDs.SUBSCRIPTION_FORM_SIGNATURE
        );
        return signatureFormStep?.signatures?.find(
            sig => sig.signatoryUserType === signatoryUserType.SERVICER
        );
    }, [subscription]);
    const currentUserIsNotSigningUser = useMemo(
        () =>
            servicerSignature
                ? internalUser?.id !== getUserIdFromIri(servicerSignature.signatoryUser)
                : true,
        [internalUser, servicerSignature]
    );

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const { data: { userList: signatoryUsers = [] } = { userList: [] }, isLoading: usersLoader } =
        useGetUsersQuery(
            userSchemas.getUsers({
                pagination: false,
                role: [
                    roles.find(r =>
                        userIsProvider
                            ? r.name === RoleNames.PROVIDER_SIGNATORY
                            : r.name === RoleNames.SERVICER_SIGNATORY
                    )?.id,
                ],
                networkAdmin: [0],
            }),
            { skip: !roles.length || !open }
        );

    const handleClose = useCallback(() => {
        dispatch(signatureDialogSet({ open: false }));
    }, [dispatch]);

    const handleOptionChange = useCallback(e => {
        setSignatureOption(e.target.value);
    }, []);

    const handleUserChange = useCallback(e => {
        setError('');
        setSelectedUser(+e.target.value);
    }, []);

    const handleOpenSignature = useCallback(
        signature => {
            downloadClick(signature.signerUrl);
            handleClose();
        },
        [handleClose]
    );

    const handleSignMyself = useCallback(() => {
        if (currentUserIsNotSigningUser) {
            updateSignature({
                id: servicerSignature.id,
                signatoryUser: `api/users/${internalUser?.id}`,
            });
        } else {
            handleOpenSignature(servicerSignature);
        }
    }, [
        updateSignature,
        handleOpenSignature,
        internalUser,
        currentUserIsNotSigningUser,
        servicerSignature,
    ]);

    const handleUpdateSignature = useCallback(() => {
        if (!servicerSignature?.id) return;

        if (signatureOption === 'me') {
            handleSignMyself(servicerSignature);
        } else {
            if (!selectedUser) {
                setError(texts.MUST_CHOOSE_SIGNER);
            } else {
                updateSignature({
                    id: servicerSignature?.id,
                    signatoryUser: `api/users/${selectedUser}`,
                });
            }
        }
    }, [signatureOption, selectedUser, updateSignature, handleSignMyself, servicerSignature]);

    const handleUpdateConvention = useCallback(() => {
        if (signatureOption === 'me') {
            updateConvention({
                id: convention?.id,
                providerSignatoryUser: `api/users/${internalUser?.id}`,
            });
        } else {
            if (!selectedUser) {
                setError(texts.MUST_CHOOSE_SIGNER);
            } else {
                updateConvention({
                    id: convention?.id,
                    providerSignatoryUser: `api/users/${selectedUser}`,
                });
            }
        }
    }, [convention, internalUser, selectedUser, signatureOption, updateConvention]);

    const handleSubmit = useCallback(() => {
        if (subscription) {
            handleUpdateSignature();
        } else {
            handleUpdateConvention();
        }
    }, [subscription, handleUpdateConvention, handleUpdateSignature]);

    useEffect(() => {
        if (signatureSuccess && signatureOption === 'me') {
            handleOpenSignature(servicerSignature);
            resetSignature();
            handleClose();
        } else if (signatureSuccess) {
            resetSignature();
            handleClose();
        }
    }, [
        signatureSuccess,
        handleClose,
        handleOpenSignature,
        signatureOption,
        resetSignature,
        servicerSignature,
    ]);

    useEffect(() => {
        setSignatureOption('me');
    }, [open]);

    useEffect(() => {
        if (isSuccess) {
            if (signatureOption === 'me') {
                const signatureObjects = jsonParseSafe(convention.signatureUrls);
                downloadClick(signatureObjects?.providerSignerUrl);
                handleClose();
            } else {
                handleClose();
            }
            reset();
        }
    }, [convention, isSuccess, handleClose, signatureOption, reset]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={texts.CHOOSE_SIGNER} handleClose={handleClose} />
            <DialogContent>
                <Loader loading={rolesLoader || usersLoader}>
                    <Box paddingX="20px">
                        <RadioGroup
                            value={signatureOption}
                            onChange={handleOptionChange}
                            sx={{ marginBottom: 2 }}
                        >
                            <RadioLabel value="me" label={texts.I_SIGN} />
                            <RadioLabel value="other" label={texts.CHOOSE_USER} />
                        </RadioGroup>
                        {signatureOption === 'other' && (
                            <CustomSelect
                                value={selectedUser}
                                onChange={handleUserChange}
                                error={!!error}
                                helperText={error}
                            >
                                {signatoryUsers.map(user => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {parseFullName(user)}
                                    </MenuItem>
                                ))}
                            </CustomSelect>
                        )}
                    </Box>
                </Loader>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={signatureLoader || conventionLoader} onClick={handleClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton
                    loading={signatureLoader || conventionLoader}
                    onClick={handleSubmit}
                    disabled={signatureLoader || conventionLoader || rolesLoader || usersLoader}
                >
                    {texts.CONFIRMER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default SignatureChoiceDialog;
